import { isValidElement, cloneElement, ReactNode } from 'react';
import capitalize from 'lodash/capitalize';

import {
  BADGE_VARIANT_ICONS,
  PlatformBadgeVariants,
  AccessBadgeVariants,
  MiscBadgeVariants,
  StatusBadgeVariants,
} from './constants';
import { BadgeProps } from './types';

const isValidIcon = (el: any): boolean => el && isValidElement(el);

/**
 * Determines the icon to use from props
 * @param {BadgeProps} props from component
 * @returns {(ReactElement|null)}
 */
const getBaseIconFromProps = ({ icon, variant }: BadgeProps): ReactNode | null | undefined => {
  if (isValidIcon(icon)) return icon;

  const variantIcon = BADGE_VARIANT_ICONS[variant];

  return isValidIcon(variantIcon) ? variantIcon : null;
};

/**
 * Gets correctly formatted icon for Badge component
 * @param {BadgeProps} props from component
 * @returns {(ReactElement|null)}
 */
export const getIconFromProps = ({ icon, variant }: BadgeProps): ReactNode | null => {
  const baseIcon = getBaseIconFromProps({ icon, variant });

  if (isValidElement(baseIcon)) {
    return cloneElement(baseIcon, {
      width: 16,
      height: 16,
      isColorInherited: true,
      // Allows any of these to be overriden by props if needed
      ...(baseIcon?.props || {}),
    });
  }

  return null;
};

/**
 * Returns default text if the variant requires it
 * @param {BADGE_VARIANT_TYPES} variant
 * @returns {(string|null)}
 */
export const getDefaultText = (variant: any): string | null => {
  if (typeof variant !== 'string') return null;

  const variantText: string | undefined = [
    ...Object.values(PlatformBadgeVariants),
    ...Object.values(StatusBadgeVariants),
    ...Object.values(AccessBadgeVariants),
    MiscBadgeVariants.BETA,
  ].find((x) => x === variant.toLowerCase());

  return variantText ? capitalize(variantText) : null;
};
